import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import {
  HelpCircle,
  Search,
  ArrowLeft,
  ChevronRight,
  MessageSquare,
  Tag,
  Shield,
  User,
  CreditCard,
  Settings,
  AlertTriangle,
  Mail,
  Phone,
} from "lucide-react";
import { Footer } from "@/components/Footer";
import { Navbar } from "@/components/Navbar";
import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";

export default function HelpCenter() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");

  const categories = [
    {
      icon: Tag,
      title: "Buying & Browsing",
      description: "Find items, make offers, and communicate with sellers",
      articles: [
        "How to search for items",
        "How to contact a seller",
        "Tips for safe buying",
        "How to save favorites",
        "Understanding ad details",
      ],
    },
    {
      icon: MessageSquare,
      title: "Selling & Posting",
      description: "Create listings, manage ads, and connect with buyers",
      articles: [
        "How to post an ad",
        "Adding photos to your listing",
        "Setting the right price",
        "Editing or deleting your ad",
        "Responding to inquiries",
      ],
    },
    {
      icon: User,
      title: "Account & Profile",
      description: "Manage your account settings and profile information",
      articles: [
        "Creating an account",
        "Updating your profile",
        "Changing your password",
        "Notification settings",
        "Deleting your account",
      ],
    },
    {
      icon: Shield,
      title: "Safety & Security",
      description: "Stay safe while buying and selling on Listo",
      articles: [
        "Safety tips for meeting in person",
        "Avoiding scams and fraud",
        "Reporting suspicious activity",
        "Protecting your personal information",
        "Our verification process",
      ],
    },
    {
      icon: CreditCard,
      title: "Payments & Transactions",
      description: "Information about payments and completing transactions",
      articles: [
        "Accepted payment methods",
        "How to complete a transaction",
        "What to do if something goes wrong",
        "Refund policies",
        "Transaction fees",
      ],
    },
    {
      icon: Settings,
      title: "Technical Support",
      description: "Troubleshooting and technical issues",
      articles: [
        "App not loading properly",
        "Image upload issues",
        "Browser compatibility",
        "Mobile app installation",
        "Clearing cache and cookies",
      ],
    },
  ];

  const faqs = [
    {
      question: "How do I post an ad?",
      answer: "To post an ad, click the 'Post Ad' button in the navigation bar. You'll need to sign in or create an account first. Then, select a category, add details about your item including photos, set a price, and publish your listing.",
    },
    {
      question: "Is it free to use Listo?",
      answer: "Yes! Posting basic ads on Listo is completely free. We offer optional premium features for sellers who want more visibility, but the core functionality is always free.",
    },
    {
      question: "How do I contact a seller?",
      answer: "On any listing page, click the 'Contact Seller' or 'Send Message' button. You'll be able to send a message directly to the seller through our secure messaging system.",
    },
    {
      question: "How do I report a suspicious listing?",
      answer: "Click the 'Report' button on any listing page. Select the reason for your report and provide any additional details. Our moderation team reviews all reports within 24 hours.",
    },
    {
      question: "Can I edit my ad after posting?",
      answer: "Yes! Go to your Dashboard, find the ad you want to edit, and click the edit button. You can update the title, description, price, photos, and other details at any time.",
    },
    {
      question: "How do I delete my account?",
      answer: "Go to your Profile settings, scroll to the bottom, and click 'Delete Account'. Please note this action is permanent and cannot be undone. All your listings and messages will be removed.",
    },
    {
      question: "What should I do if I encounter a scam?",
      answer: "Report the listing and user immediately using our report function. Do not proceed with the transaction. If you've already lost money, contact your local authorities and our support team.",
    },
    {
      question: "How long do ads stay active?",
      answer: "Ads remain active for 30 days by default. You can renew them from your Dashboard before they expire. Sold items should be marked as sold to remove them from search results.",
    },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <Navbar />

      <div className="container flex justify-between items-center h-12">
        <Link href="/">
          <a className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </a>
        </Link>
      </div>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-16 overflow-hidden bg-muted/30">
          <div className="container">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
                <HelpCircle className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-4xl font-bold mb-4">{t('footer.help')}</h1>
              <p className="text-xl text-muted-foreground mb-8">
                Find answers to common questions and learn how to get the most out of Listo.
              </p>
              
              {/* Search */}
              <div className="relative max-w-xl mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search for help..."
                  className="pl-12 h-14 text-lg"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Links */}
        <section className="py-12 border-b border-border">
          <div className="container">
            <div className="flex flex-wrap justify-center gap-4">
              <Link href="/post">
                <a className="btn-secondary text-sm">Post an Ad</a>
              </Link>
              <Link href="/safety-guidelines">
                <a className="btn-secondary text-sm">Safety Guidelines</a>
              </Link>
              <Link href="/contact">
                <a className="btn-secondary text-sm">Contact Support</a>
              </Link>
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-16">
          <div className="container">
            <h2 className="text-2xl font-bold mb-8 text-center">Browse by Topic</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <div
                  key={category.title}
                  className="card-elegant p-6 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <category.icon className="w-10 h-10 text-primary mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{category.title}</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {category.description}
                  </p>
                  <ul className="space-y-2">
                    {category.articles.slice(0, 3).map((article) => (
                      <li key={article}>
                        <a href="#" className="text-sm text-primary hover:underline flex items-center gap-1">
                          <ChevronRight className="w-3 h-3" />
                          {article}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 bg-muted/30">
          <div className="container max-w-3xl">
            <h2 className="text-2xl font-bold mb-8 text-center">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="card-elegant px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Contact Support */}
        <section className="py-16">
          <div className="container max-w-4xl">
            <div className="card-elegant p-8 text-center">
              <AlertTriangle className="w-12 h-12 text-primary mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4">Still need help?</h2>
              <p className="text-muted-foreground mb-6">
                Can't find what you're looking for? Our support team is here to help.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="mailto:support@listomarket.app"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email Support
                </a>
                <Link href="/contact">
                  <a className="btn-primary inline-flex items-center justify-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Contact Us
                  </a>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
